import { ReactNode } from 'react'
import { FormGroup, Modal } from 'react-bootstrap'
import FormHandler from '../../utils/Form/FormHandler'
import Field from '../../utils/Form/Widgets/Field'
import AutocompleteSelect from '../../utils/Form/Widgets/AutocompleteSelect'
import Check from '../../utils/Form/Widgets/Check'
import Select from '../../utils/Form/Widgets/Select'
import { askEditScope, deleteEventWithScope, RecurrenceScope } from './recurrence'

type Props = {
  onHide: () => void
  show: boolean
  itemIdForUpdate?: string
  refreshFunction?: Function
}
class Events {
  name?: string
  description?: string
  date?: string
  start_time?: string
  end_time?: string
  event_type?: string
  all_day?: boolean
  frequency?: 'one_time' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom'
  company_site?: number
  status?: 'created' | 'completed' | 'cancelled'
  parent_event?: number = 0
}
class EventEditModal extends FormHandler {
  constructor(props: Props) {
    super(props)
    this.state = { entity: new Events() }
  }

  // Frequency as loaded from the server and the scope chosen in the "recurring event" dialog.
  private loadedFrequency?: string
  private loadedFor?: string
  private recurrenceScope: RecurrenceScope = 'this'

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<{}>, snapshot?: any): void {
    super.componentDidUpdate(prevProps, prevState, snapshot)
    const entity = (this.state as any).entity
    if (entity?.recurrence && this.loadedFor !== this.props.itemIdForUpdate) {
      this.loadedFor = this.props.itemIdForUpdate
      this.loadedFrequency = entity.frequency
    }
  }

  async submitWithScope(event: any): Promise<void> {
    event.preventDefault()
    const entity = (this.state as any).entity
    const isNew = this.props.itemIdForUpdate === 'add'
    const scope = isNew
      ? 'this'
      : await askEditScope(
          entity.recurrence,
          this.loadedFrequency !== undefined && entity.frequency !== this.loadedFrequency
        )
    if (scope === null) {
      return
    }
    this.recurrenceScope = scope
    const response = await this.handleSubmit(event)
    if (this.props.refreshFunction) {
      const id =
        (response && response.data && response.data.data && response.data.data.entity_id) ||
        (response && response.data && response.data.data && response.data.data.id) ||
        this.props.itemIdForUpdate
      this.props.refreshFunction(id)
    }
    this.props.onHide()
  }

  getApiUrl(): string {
    return '/events/save/' + this.props.itemIdForUpdate
  }
  getSingleUrl() {
    if (this.props.itemIdForUpdate === 'add') {
      return null
    } else {
      return '/events/single/' + this.props.itemIdForUpdate
    }
  }

  getPostData(): object {
    var state = this.state as any
    if (state.entity.all_day === true) {
      state.entity.start_time = '00:00:00'
      state.entity.end_time = '23:59:59'
    }
    // `recurrence` is read-only information from the server, not an event field
    const { recurrence, ...eventData } = state.entity
    return {
      events: eventData,
      recurrence_scope: this.recurrenceScope,
    }
  }
  render(): ReactNode {
    var state = this.state as any
    return (
      <Modal
        aria-hidden='true'
        dialogClassName='modal-dialog modal-dialog-centered'
        size='lg'
        show={this.props.show}
        backdrop={true}
        onHide={this.props.onHide}
      >
        <form
          onSubmit={(event) => {
            this.submitWithScope(event)
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>{this.props.itemIdForUpdate === 'add' ? 'Add' : 'Edit'} Event</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <FormGroup>
              <AutocompleteSelect
                title='Company'
                name='company'
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.company || 0}
                autoCompleteTokenUrl='/company/autocomplete'
              />
            </FormGroup>
            <FormGroup>
              <AutocompleteSelect
                title='Company Site'
                name='company_site'
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.company_site || 0}
                autoCompleteTokenUrl='/company/sites/autocomplete'
              />
            </FormGroup>
            <FormGroup>
              <AutocompleteSelect
                title='Event Type'
                name='event_type'
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.event_type || 0}
                autoCompleteTokenUrl='/event_types/autocomplete'
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Name'
                className='form-control'
                type='text'
                name='name'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.name}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Description'
                className='form-control'
                type='text'
                name='description'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.description}
              />
            </FormGroup>
            <FormGroup>
              <Field
                title='Date'
                className='form-control'
                type='date'
                name='date'
                onInput={(event: any) => {
                  this.handleInputChange(event)
                }}
                defaultValue={state.entity.date}
              />
            </FormGroup>
            <FormGroup>
              <Check
                title='All Day'
                name='all_day'
                type='switch'
                options={['']}
                checked={state.entity.all_day ? true : false}
                onChange={(event: any) => {
                  const newValue = event.target.checked ? true : false
                  this.handleInputChange({ target: { name: 'all_day', value: newValue } } as any)
                }}
              />
            </FormGroup>
            {state.entity.all_day !== true && (
              <>
                <FormGroup>
                  <Field
                    title='Start Time'
                    className='form-control'
                    type='time'
                    name='start_time'
                    onInput={(event: any) => {
                      this.handleInputChange(event)
                    }}
                    defaultValue={state.entity.start_time}
                  />
                </FormGroup>
                <FormGroup>
                  <Field
                    title='End Time'
                    className='form-control'
                    type='time'
                    name='end_time'
                    onInput={(event: any) => {
                      this.handleInputChange(event)
                    }}
                    defaultValue={state.entity.end_time}
                  />
                </FormGroup>
              </>
            )}
            <FormGroup>
              <div className='mb-3'>
                <label className='form-label fw-bold'>Frequency</label>
                <div className='d-flex flex-wrap gap-3 mt-2'>
                  {[
                    { value: 'one_time', label: 'One Time', icon: 'fa-calendar-check' },
                    { value: 'daily', label: 'Daily', icon: 'fa-calendar-day' },
                    { value: 'weekly', label: 'Weekly', icon: 'fa-calendar-week' },
                    { value: 'monthly', label: 'Monthly', icon: 'fa-calendar-alt' },
                    { value: 'yearly', label: 'Yearly', icon: 'fa-calendar' },
                  ].map((option) => (
                    <div
                      key={option.value}
                      className='form-check form-check-custom form-check-solid'
                    >
                      <input
                        className='form-check-input'
                        type='radio'
                        name='frequency'
                        id={`frequency_${option.value}`}
                        value={option.value}
                        checked={state.entity.frequency === option.value}
                        onChange={(event: any) => {
                          this.handleInputChange(event)
                        }}
                      />
                      <label
                        className='form-check-label d-flex align-items-center'
                        htmlFor={`frequency_${option.value}`}
                      >
                        <i className={`fa ${option.icon} me-2 text-secondary`}></i>
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </FormGroup>
            <FormGroup>
              <Select
                title='Status'
                name='status'
                options={[
                  { value: 'created', label: 'Created' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'cancelled', label: 'Cancelled' },
                ]}
                defaultValue={state.entity.status}
                onChange={(event: any) => {
                  this.handleInputChange(event)
                }}
              />
            </FormGroup>
          </Modal.Body>
          <Modal.Footer className='justify-content-end'>
            {this.props.itemIdForUpdate !== 'add' ? (
              <button
                type='button'
                onClick={(event) => {
                  event.preventDefault()
                  deleteEventWithScope(this.props.itemIdForUpdate as string).then((deleted) => {
                    if (deleted) {
                      if (this.props.refreshFunction) {
                        this.props.refreshFunction('')
                      }
                      this.props.onHide()
                    }
                  })
                }}
                className='btn btn-danger btn-sm'
              >
                <span className='fa fa-trash'></span>
                Delete
              </button>
            ) : (
              <></>
            )}
            <button type='submit' className='btn btn-success btn-sm'>
              <span className='fa fa-floppy-save'></span>
              Save
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    )
  }
}

export default EventEditModal
